import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { collection, getDocs } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import {
  LuArrowDown,
  LuArrowUp,
  LuArrowUpDown,
  LuChartColumn,
  LuCircleCheck,
  LuFileSpreadsheet,
  LuFlame,
  LuLeaf,
  LuPackageOpen,
  LuSearchX,
  LuTruck,
  LuWarehouse,
} from 'react-icons/lu';
import { db } from '../../config/firebaseConfig';
import {
  filterMovementsByDate,
  formatPercent,
  formatQuantity,
  summarizeByEmployee,
  summarizeByScope,
  summarizeByStock,
  summarizeTotals,
} from '../../utils/stockAnalytics';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import SegmentedControl from '../../components/ui/SegmentedControl';
import FilterTiles from '../../components/data/FilterTiles';
import ToggleChip from '../../components/data/ToggleChip';
import { TextInput } from '../../components/form/FormControls';
import { EmptyState, LoadingRows, SearchField } from '../ProfilePage/components/ProfileUi';
import useStockText from './stockText';

// Her sekme: hangi özeti ürettiği, hangi sütunları gösterdiği ve varsayılan
// sıralaması. Tablolar tek bir bileşenden çizildiği için sekme eklemek bu
// listeye bir satır eklemekten ibaret.
const TABS = [
  { key: 'employee', label: 'Çalışanlar', nameLabel: 'Çalışan' },
  { key: 'customer', label: 'Müşteriler', nameLabel: 'Müşteri' },
  { key: 'project', label: 'Projeler', nameLabel: 'Proje' },
  { key: 'stock', label: 'Malzemeler', nameLabel: 'Malzeme' },
];

const COLUMNS = {
  employee: [
    { key: 'employeeName', label: 'Çalışan', type: 'text' },
    { key: 'assigned', label: 'Verilen', type: 'number' },
    { key: 'used', label: 'Kullanılan', type: 'number' },
    { key: 'onHand', label: 'Elinde', type: 'number' },
    { key: 'usageRate', label: 'Kullanım', type: 'rate' },
    { key: 'visitCount', label: 'Vizit', type: 'number' },
    { key: 'perVisit', label: 'Vizit başına', type: 'number' },
  ],
  customer: [
    { key: 'label', label: 'Müşteri', type: 'text' },
    { key: 'assigned', label: 'Gönderilen', type: 'number' },
    { key: 'used', label: 'Kullanılan', type: 'number' },
    { key: 'onHand', label: 'Sahada', type: 'number' },
    { key: 'remaining', label: 'Depoda kalan', type: 'number' },
    { key: 'usageRate', label: 'Kullanım', type: 'rate' },
    { key: 'stockCount', label: 'Malzeme', type: 'number' },
  ],
  project: [
    { key: 'label', label: 'Proje', type: 'text' },
    { key: 'assigned', label: 'Gönderilen', type: 'number' },
    { key: 'used', label: 'Kullanılan', type: 'number' },
    { key: 'onHand', label: 'Sahada', type: 'number' },
    { key: 'remaining', label: 'Depoda kalan', type: 'number' },
    { key: 'usageRate', label: 'Kullanım', type: 'rate' },
    { key: 'stockCount', label: 'Malzeme', type: 'number' },
  ],
  stock: [
    { key: 'stockName', label: 'Malzeme', type: 'text' },
    { key: 'customerName', label: 'Müşteri', type: 'text' },
    { key: 'projectName', label: 'Proje', type: 'text' },
    { key: 'assigned', label: 'Dağıtılan', type: 'number' },
    { key: 'used', label: 'Kullanılan', type: 'number' },
    { key: 'remaining', label: 'Depoda kalan', type: 'number' },
    { key: 'employeeCount', label: 'Kişi', type: 'number' },
  ],
};

const DEFAULT_SORT = {
  employee: 'assigned',
  customer: 'assigned',
  project: 'assigned',
  stock: 'used',
};

const searchableText = (row) =>
  [row.employeeName, row.label, row.stockName, row.customerName, row.projectName]
    .filter(Boolean)
    .join(' ')
    .toLocaleLowerCase('tr-TR');

/**
 * Stok kullanım raporu.
 *
 * "Kime ne kadar verildi, ne kadar kullanıldı, ne kadar kaldı" sorusunu tek
 * ekranda cevaplar. Veri iki kaynaktan gelir: StockMovements (atama + vizit
 * kullanımı) ve Stocks (depodaki güncel kalan). Hesaplama ve Excel (Türkçe
 * başlıklar) aynen korundu; görünüm Tailwind ve TR/EN.
 */
const StockReportModal = ({ show, onClose }) => {
  const { t, language, label } = useStockText();
  const [movements, setMovements] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('employee');
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [onlyConsumables, setOnlyConsumables] = useState(true);
  const [sort, setSort] = useState({ key: DEFAULT_SORT.employee, direction: 'desc' });

  useEffect(() => {
    if (!show) return undefined;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [movementSnap, stockSnap] = await Promise.all([
          getDocs(collection(db, 'StockMovements')),
          getDocs(collection(db, 'Stocks')),
        ]);
        if (cancelled) return;
        setMovements(movementSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setStocks(stockSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (error) {
        console.error('Stok raporu yüklenemedi:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [show]);

  // Sarf malzemesi süzgeci: müşteri/proje bağlantısı yalnızca sarf için var
  const scopedStocks = useMemo(
    () => (onlyConsumables ? stocks.filter((s) => s['Kategori'] === 'sarf') : stocks),
    [stocks, onlyConsumables]
  );

  const scopedMovements = useMemo(() => {
    const dated = filterMovementsByDate(movements, startDate, endDate);
    if (!onlyConsumables) return dated;

    const consumableIds = new Set(scopedStocks.map((s) => s.id));
    const knownIds = new Set(stocks.map((s) => s.id));
    // Stok kartı silinmiş hareketlerin kategorisi bilinemez; rapordan
    // düşürmek yerine gösteriyoruz ki geçmiş tüketim kaybolmasın.
    return dated.filter(
      (m) => !m.stockId || consumableIds.has(m.stockId) || !knownIds.has(m.stockId)
    );
  }, [movements, scopedStocks, stocks, startDate, endDate, onlyConsumables]);

  const totals = useMemo(
    () => summarizeTotals(scopedMovements, scopedStocks),
    [scopedMovements, scopedStocks]
  );

  const rows = useMemo(() => {
    switch (activeTab) {
      case 'customer':
        return summarizeByScope(scopedMovements, scopedStocks, 'customer');
      case 'project':
        return summarizeByScope(scopedMovements, scopedStocks, 'project');
      case 'stock':
        return summarizeByStock(scopedMovements, scopedStocks);
      case 'employee':
      default:
        return summarizeByEmployee(scopedMovements);
    }
  }, [activeTab, scopedMovements, scopedStocks]);

  const visibleRows = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('tr-TR');
    const filtered = term ? rows.filter((row) => searchableText(row).includes(term)) : rows;

    const column = COLUMNS[activeTab].find((c) => c.key === sort.key);
    const direction = sort.direction === 'asc' ? 1 : -1;

    return [...filtered].sort((a, b) => {
      const aValue = a[sort.key];
      const bValue = b[sort.key];
      if (column?.type === 'text') {
        return direction * String(aValue || '').localeCompare(String(bValue || ''), 'tr');
      }
      return direction * ((Number(aValue) || 0) - (Number(bValue) || 0));
    });
  }, [rows, search, sort, activeTab]);

  // "Kim daha dikkatli kullanıyor": vizit başına en az tüketen. Karşılaştırma
  // ancak vizit yapmış kişiler arasında anlamlı olduğu için diğerleri elenir.
  const highlights = useMemo(() => {
    if (activeTab !== 'employee') return null;
    // employeeId taşımayan hareketler "Bilinmeyen çalışan" altında toplanır;
    // bu gerçek bir kişi olmadığı için kıyaslamaya girmez.
    const withVisits = rows.filter(
      (row) => row.employeeId && row.visitCount > 0 && row.used > 0
    );
    if (withVisits.length < 2) return null;
    const sorted = [...withVisits].sort((a, b) => a.perVisit - b.perVisit);
    return { careful: sorted[0], heavy: sorted[sorted.length - 1] };
  }, [rows, activeTab]);

  const toggleSort = (key) => {
    setSort((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === 'desc' ? 'asc' : 'desc' }
        : { key, direction: 'desc' }
    );
  };

  const changeTab = (key) => {
    setActiveTab(key);
    setSort({ key: DEFAULT_SORT[key], direction: 'desc' });
  };

  const exportExcel = () => {
    const columns = COLUMNS[activeTab];
    const data = visibleRows.map((row) => {
      const record = {};
      columns.forEach((column) => {
        record[column.label] =
          column.type === 'rate' ? formatPercent(row[column.key]) : row[column.key] ?? '';
      });
      return record;
    });
    const sheet = XLSX.utils.json_to_sheet(data);
    const book = XLSX.utils.book_new();
    const tabLabel = TABS.find((t) => t.key === activeTab)?.label || 'Rapor';
    XLSX.utils.book_append_sheet(book, sheet, tabLabel);
    XLSX.writeFile(book, `Stok_Raporu_${tabLabel}.xlsx`);
  };

  const columns = COLUMNS[activeTab];
  const columnLabel = (column) => label(`report.columns.${activeTab}.${column.key}`);

  const renderCell = (row, column) => {
    const value = row[column.key];

    if (column.type === 'rate') {
      const percent = Math.min(100, Math.round((value || 0) * 100));
      return (
        <span className="tw-flex tw-items-center tw-justify-end tw-gap-2" title={label('report.usedRate', { percent: formatPercent(value) })}>
          <span className="tw-h-1.5 tw-w-16 tw-overflow-hidden tw-rounded-full tw-bg-slate-200/80 dark:tw-bg-white/10">
            <span
              className={`tw-block tw-h-full tw-rounded-full ${percent >= 90 ? 'tw-bg-rose-500' : 'tw-bg-teal-500'}`}
              style={{ width: `${percent}%` }}
            />
          </span>
          <span className="tw-w-10 tw-text-right tw-font-semibold tw-tabular-nums">{formatPercent(value)}</span>
        </span>
      );
    }

    if (column.type === 'number') {
      return <span className="tw-tabular-nums">{formatQuantity(value)}</span>;
    }

    // Malzeme sekmesinde düşük stok uyarısı ada bitişik durur
    if (column.key === 'stockName' && row.isLow) {
      return (
        <span className="tw-inline-flex tw-items-center tw-gap-2 tw-font-medium tw-text-slate-900 dark:tw-text-white">
          {value || '-'}
          <span className="tw-rounded-full tw-bg-rose-500/10 tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wide tw-text-rose-600 dark:tw-text-rose-300">{label('report.critical')}</span>
        </span>
      );
    }

    return <span className="tw-font-medium tw-text-slate-900 dark:tw-text-white">{value || '—'}</span>;
  };

  const sortIcon = (column) => {
    if (sort.key !== column.key) return <LuArrowUpDown aria-hidden="true" className="tw-h-3 tw-w-3 tw-opacity-40" />;
    return sort.direction === 'desc'
      ? <LuArrowDown aria-hidden="true" className="tw-h-3 tw-w-3 tw-text-teal-600 dark:tw-text-teal-300" />
      : <LuArrowUp aria-hidden="true" className="tw-h-3 tw-w-3 tw-text-teal-600 dark:tw-text-teal-300" />;
  };

  const summaryTiles = [
    { key: 'assigned', label: label('report.summary.assigned'), value: formatQuantity(totals.assigned), icon: LuTruck, tone: 'info', static: true },
    { key: 'used', label: label('report.summary.used'), value: formatQuantity(totals.used), icon: LuCircleCheck, tone: 'success', static: true },
    { key: 'onHand', label: label('report.summary.onHand'), value: formatQuantity(totals.onHand), icon: LuPackageOpen, tone: 'warning', static: true },
    { key: 'remaining', label: label('report.summary.remaining'), value: formatQuantity(totals.remaining), icon: LuWarehouse, tone: 'violet', static: true },
  ];

  return (
    <Modal
      isOpen={show}
      onClose={onClose}
      size="full"
      title={label('report.title')}
      subtitle={label('report.subtitle')}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-teal-500 tw-to-emerald-700 tw-shadow-teal-600/30" size="xs">
          <LuChartColumn aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          <span className="tw-mr-auto tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
            <strong className="tw-font-semibold tw-text-slate-700 dark:tw-text-slate-200">{label('report.records', { count: visibleRows.length })}</strong>
            <span className="tw-hidden sm:tw-inline"> · {label('report.hint')}</span>
          </span>
          <Button onClick={onClose}>{t('common.close')}</Button>
        </>
      )}
    >
      <div className="tw-space-y-4">
        <FilterTiles tiles={summaryTiles} label={label('report.summary.label')} className="tw-grid-cols-2 lg:tw-grid-cols-4" />

        <div className="tw-flex tw-flex-col tw-gap-3 xl:tw-flex-row xl:tw-items-center xl:tw-justify-between">
          <div className="tw-max-w-full tw-overflow-x-auto">
            <SegmentedControl
              options={TABS.map((tab) => ({ value: tab.key, label: label(`report.tabs.${tab.key}`) }))}
              value={activeTab}
              onChange={changeTab}
              layoutId="stock-report-tabs"
              label={label('report.tabs.label')}
            />
          </div>
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            <ToggleChip
              pressed={onlyConsumables}
              onChange={setOnlyConsumables}
              label={label('report.onlyConsumables')}
              title={label('report.onlyConsumablesHint')}
            />
            <TextInput
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              title={label('report.startDate')}
              aria-label={label('report.startDate')}
              className="tw-w-auto sm:tw-w-40"
            />
            <TextInput
              type="date"
              value={endDate}
              min={startDate || undefined}
              onChange={(e) => setEndDate(e.target.value)}
              title={label('report.endDate')}
              aria-label={label('report.endDate')}
              className="tw-w-auto sm:tw-w-40"
            />
            <SearchField value={search} onChange={setSearch} placeholder={label('report.search')} className="tw-min-w-[12rem] tw-flex-1 xl:tw-w-56 xl:tw-flex-none" />
            <Button onClick={exportExcel}>
              <LuFileSpreadsheet aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-emerald-600 dark:tw-text-emerald-400" />
              {label('report.excel')}
            </Button>
          </div>
        </div>

        {/* Öne çıkanlar: vizit başına en az ve en çok tüketen */}
        {highlights && (
          <div className="tw-grid tw-gap-3 md:tw-grid-cols-2">
            <div className="tw-flex tw-items-start tw-gap-3 tw-rounded-2xl tw-border tw-border-emerald-500/25 tw-bg-emerald-500/[0.07] tw-p-3.5 tw-text-sm tw-text-emerald-900 dark:tw-text-emerald-100">
              <LuLeaf aria-hidden="true" className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 tw-text-emerald-600 dark:tw-text-emerald-300" />
              <span>{label('report.careful', { name: highlights.careful.employeeName, amount: formatQuantity(highlights.careful.perVisit) })}</span>
            </div>
            <div className="tw-flex tw-items-start tw-gap-3 tw-rounded-2xl tw-border tw-border-orange-500/25 tw-bg-orange-500/[0.07] tw-p-3.5 tw-text-sm tw-text-orange-900 dark:tw-text-orange-100">
              <LuFlame aria-hidden="true" className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 tw-text-orange-600 dark:tw-text-orange-300" />
              <span>{label('report.heavy', { name: highlights.heavy.employeeName, amount: formatQuantity(highlights.heavy.perVisit) })}</span>
            </div>
          </div>
        )}

        {loading ? (
          <LoadingRows count={6} height="tw-h-11" label={label('report.loading')} />
        ) : visibleRows.length === 0 ? (
          <EmptyState icon={LuSearchX} title={label('report.empty')} compact />
        ) : (
          <div className="tw-relative tw-max-h-[55vh] tw-overflow-auto tw-rounded-2xl tw-border tw-border-slate-200/80 glass-scrollbar dark:tw-border-white/10">
            <table className="tw-w-full tw-border-collapse tw-text-sm">
              <thead className="tw-sticky tw-top-0 tw-z-[1] tw-bg-slate-50 dark:tw-bg-[#262a32]">
                <tr>
                  {columns.map((column) => (
                    <th key={column.key} scope="col" aria-sort={sort.key === column.key ? (sort.direction === 'desc' ? 'descending' : 'ascending') : 'none'} className={`tw-whitespace-nowrap tw-px-4 tw-py-0 ${column.type === 'text' ? 'tw-text-left' : 'tw-text-right'}`}>
                      <button
                        type="button"
                        onClick={() => toggleSort(column.key)}
                        className={`tw-inline-flex tw-h-11 tw-items-center tw-gap-1.5 tw-bg-transparent tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 hover:tw-text-slate-800 dark:tw-text-slate-400 dark:hover:tw-text-white ${column.type === 'text' ? '' : 'tw-flex-row-reverse'}`}
                      >
                        {columnLabel(column)}
                        {sortIcon(column)}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="tw-divide-y tw-divide-slate-200/70 tw-text-slate-700 dark:tw-divide-white/[0.06] dark:tw-text-slate-200">
                {visibleRows.map((row) => (
                  <tr key={row.key} className="tw-bg-white hover:tw-bg-slate-50 dark:tw-bg-[#1f232b] dark:hover:tw-bg-[#262a32]">
                    {columns.map((column) => (
                      <td key={column.key} className={`tw-whitespace-nowrap tw-px-4 tw-py-2.5 ${column.type === 'text' ? '' : 'tw-text-right'}`}>
                        {renderCell(row, column)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
};

StockReportModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default StockReportModal;
